<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("purchase_return") }}</h4>
        </div>
      </div>
      <ul class="table-top-head">
        <li>
          <div class="page-btn">
            <router-link to="/purchase/return/list" class="btn btn-added">
              <span>Back</span>
            </router-link>
          </div>
        </li>
        <li>
          <a data-bs-toggle="tooltip" data-bs-placement="top" title="Collapse" id="collapse-header"><i class="ti ti-chevron-up"></i></a>
        </li>
      </ul>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="submitPurchaseReturn" enctype="multipart/form-data">
        <!-- Header Form Section -->
        <div class="row gy-2 gx-4 mb-3 justify-content-between align-items-center">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:200px;">{{$t('receiving_no')}}:</label>
            <input type="text" class="form-control" v-model="item.receiving_no" readonly/>
          </div>

          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:110px;">{{$t('receiving_date')}}:</label>
            <input type="date" class="form-control" v-model="item.receiving_date" @change="fetchReceivingNo" />
            <div v-if="errors.receiving_date" class="text-danger mt-2">
              <span>{{errors.receiving_date}}</span>
            </div>
          </div>

          <div class="col-md-6 d-flex align-items-start">
            <label class="form-label small mb-0 me-3" style="width:130px;">{{$t('select_voucher')}}:</label>
            <div class="flex-grow-1 position-relative">
              <!-- Search Input -->
              <input
                type="number"
                v-model="q"
                @input="searchVoucher"
                :placeholder="$t('search_voucher_by')"
                class="form-control"
                @keydown="handleKeysVoucher"
              />

              <!-- Dropdown Search Results -->
              <div
                v-if="q"
                class="search-results shadow rounded bg-white position-absolute mt-1"
                style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                :class="{ 'd-block': voucher_result.length > 0, 'd-none': voucher_result.length === 0 }"
              >

                <!-- Results List -->
                <ul class="list-group list-group-flush">
                  <li
                    v-for="(voucher, index) in voucher_result"
                    :key="voucher.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'bg-primary text-white': index === itemIndex }"
                    style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                  >
                    <!-- Clickable Product Info -->
                    <div
                      class="flex-grow-1 d-flex flex-column"
                      @click="selectVoucher(voucher)"
                      style="cursor: pointer;"
                    >
                      <div class="d-flex align-items-center gap-2">
                        <!-- Item Info -->
                        <div class="d-flex flex-column">
                          <strong class="text-dark">{{ voucher.voucher_id }} - {{ voucher.voucher_date }}</strong>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <!-- No Results -->
                <div v-if="!voucher_result.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                  No results found
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6 d-flex align-items-start">
            <label class="form-label small mb-0 me-3" style="width:85px;">{{$t('select_item')}}:</label>
            <div class="flex-grow-1 position-relative">
              <!-- Search Input -->
              <input
                type="text"
                v-model="query"
                @input="searchItems"
                :placeholder="$t('search_item_by_name_barcode')"
                class="form-control"
                @keydown="handleKeys"
                :disabled="!selectedVoucher"
              />

              <!-- Dropdown Search Results -->
              <div
                v-if="query"
                class="search-results shadow rounded bg-white position-absolute mt-1"
                style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
              >

                <!-- Results List -->
                <ul class="list-group list-group-flush">
                  <li
                    v-for="(item, index) in results"
                    :key="item.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'bg-primary text-white': index === itemIndex }"
                    style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                  >
                    <!-- Clickable Product Info -->
                    <div
                      class="flex-grow-1 d-flex flex-column"
                      @click="selectItem(item)"
                      style="cursor: pointer;"
                    >
                      <div class="d-flex align-items-center gap-2">
                        <!-- Image Wrapper -->
                        <div
                          style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;"
                        >
                          <img
                            :src="getImageUrl(item.images)"
                            alt="Items"
                            style="width:100%; height:100%; object-fit:cover;"
                          />
                        </div>

                        <!-- Item Info -->
                        <div class="d-flex flex-column">
                          <strong class="text-dark">{{ item.item_name }} - {{ item.item_code }}</strong>
                          <span
                            style="font-size: 0.75rem; width: fit-content; color: black"
                          >
                            Challan Qty: {{ item.total_qty }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <!-- No Results -->
                <div v-if="!results.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                  No results found
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive gy-2 gx-4 mb-3 p-3 fixed-table">
          <table class="table table-bordered align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 14.28%">{{$t('item_name')}}</th>
                <th style="width: 14.28%">{{$t('unit_price')}}</th>
                <th style="width: 14.28%">{{$t('quantity')}}</th>
                <th style="width: 14.28%">{{$t('total_price')}}</th>
                <th style="width: 14.28%">{{$t('return_qty')}}</th>
                <th style="width: 14.28%">{{$t('return_total_price')}}</th>
                <th style="width: 14.28%" >{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cust, index) in addedItems" :key="index" v-if="selectedItem" class="readonly-cell">
                <td class="readonly-cell">{{ cust.item_name }}</td>
                <td class="readonly-cell">{{ cust.unit_price }}</td>
                <td class="readonly-cell">{{ cust.quantity }}</td>
                <td class="readonly-cell">{{ cust.total_price }}</td>
                <td class="dynamic-cell">
                  <input
                    type="number"
                    class="form-control"
                    v-model.number="cust.return_qty"
                    @input="validateReturnQty(cust)"
                    min="1"
                    :max="cust.quantity"
                  />
                </td>
                <td class="dynamic-cell">{{ cust.return_total_price }}</td>
                <td class="dynamic-cell">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeItem(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <!-- Show empty row if no data -->
              <tr v-if="!addedItems || !addedItems.length">
                <td colspan="11" class="text-center text-muted">No Items added</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment type and Total in One Row -->
        <div class="d-flex justify-content-between align-items-start mt-4">
          <div class="d-flex gap-4">
            <!-- Payment Type -->
            <div class="d-flex flex-column">
              <label class="form-label">
                {{$t('payment_type')}} <span class="text-danger">*</span>
              </label>
              <VueSelect2
                v-model="item.cash_type"
                :options="formattedcashtypes"
                :settings="{ allowClear: true, tags: true }"
                @change="onChange"
                class="form-control"
                style="width: 300px;"
              />
              <div v-if="errors.cash_type" class="text-danger">{{ errors.cash_type }}</div>
            </div>

            <!-- Bank Accounts -->
            <div v-if="isBank" class="d-flex flex-column">
              <label class="form-label">
                {{$t('bank_account')}} <span class="text-danger">*</span>
              </label>
              <VueSelect2
                v-model="item.bank_account"
                :options="formattedbankaccounts"
                :settings="{ allowClear: true, tags: true }"
                @change="onChange"
                class="form-control"
                style="width: 300px;"
              />
              <div v-if="errors.bank_account" class="text-danger">{{ errors.bank_account }}</div>
            </div>
          </div>

          <!-- Total Amount -->
          <div class="d-flex flex-column pe-2">
            <label class="form-label ">Total amount :</label>
            <input
              type="text"
              class="form-control text-end"
              :value="totalAmount"
              disabled
              style="width: 200px; height: 38px; font-size: 1rem; font-weight: bold;"
            />
          </div>
        </div>

        <!-- Buttons Row -->
        <div class="d-flex justify-content-end mt-3">
          <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
          <button :disabled="!isValidReturn()" type="submit" class="btn btn-submit" @click="onSubmit">{{ $t('submit') }}</button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import VueSelect2 from "vue3-select2-component";
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';

export default {
  components: {
    Vue3Datepicker,
    VueSelect2,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      item: {
        item_id: null, voucher_date: '', voucher_id: '', item: null, quantity: 0, total_price: 0, unit_price: 0,
        receiving_no: '', receiving_date: new Date().toISOString().split('T')[0], cash_type: null,
        bank_account: '', return_qty: 1, return_total_price: 0, total_stock: 0, total_qty: 0
      },

      errors: {},
      errorMessage: "",
      query: "",
      q: "",
      results: [],
      itemIndex: 0,
      selectedItem: null,
      selectedVoucher: null,
      addedItems: [],
      voucher_result: [],
      cash_types: [],
      bank_accounts: [],
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchReceivingNo();
  },

  computed: {
    formattedcashtypes() {
      return this.cash_types.map(cash_type => ({ id: cash_type.id, text: cash_type.name }));
    },

    formattedbankaccounts() {
      return this.bank_accounts.map(bank_account => ({ id: bank_account.id, text: bank_account.name }));
    },

    isBank() {
      const val = this.item.cash_type;
      const id = val && typeof val === 'object' ? val.id : val;
      return String(id) === '1010102';
    },

    totalAmount() {
      return this.addedItems.reduce((sum, item) => {
        const amount = parseFloat(item.return_total_price) || 0;
        return sum + amount;
      }, 0).toFixed(2);
    },
  },

  watch: {
    'item.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.item.receiving_no = '';
      }
    },

    'item.cash_type'(newVal) {
      const id = newVal && typeof newVal === 'object' ? newVal.id: newVal;
      if (String(id) === '1010102') {
        if (this.bank_accounts.length > 0) {
          this.item.bank_account = this.bank_accounts[0].id;
        }
      } else {
        this.item.bank_account = null;
      }
    },

    addedItems: {
      deep: true,
        handler(newItems) {
          newItems.forEach(item => {
          if (isNaN(item.return_qty) || item.return_qty < 1) {
            item.return_qty = 1;
          }
          if (item.return_qty > item.quantity) {
            item.return_qty = item.quantity;
          }
        });
      }
    }
  },

  methods: {
    async fetchDropdownData() {
      try {
        const [
          cash_typeRes, receive_noRes, bank_accRes,
        ] = await Promise.all([
          this.$axios.get(apiUrls.batch_cash_type.get),
          this.$axios.get(apiUrls.purchase_return_receive_number.get),
          this.$axios.get(apiUrls.batch_bank_account.get)
        ]);

        this.cash_types = cash_typeRes?.data || [];
        this.item.receiving_no = receive_noRes?.data?.receiving_no || '';
        this.bank_accounts = bank_accRes?.data || [];

        if (this.cash_types.length > 0 && !this.item.cash_type) {
          this.item.cash_type = this.cash_types[0].id;
        }

        if (this.bank_accounts.length > 0 && !this.item.bank_account) {
          this.item.bank_account = this.bank_accounts[0].id;
        }

      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    },

    async searchVoucher() {
      if (!this.q) {
        this.voucher_result = [];
        this.results = [];
        this.query = '';
        return
      }

      try {
        const response = await this.$axios.get(apiUrls.purchase_voucher_search.get, {
          params: { q: this.q }
        })
        this.voucher_result = response.data.results;
        this.itemIndex = 0;

        // render feather icons
        await this.$nextTick(() => {
          if (window.feather) feather.replace()
        })
      } catch (error) {
        this.voucher_result = [];
        this.results = [];
        this.addedItems = [];
        this.query = '';
      }
    },

    selectVoucher(voucher) {
      this.selectedVoucher = voucher;
      this.q = voucher.voucher_id;
      this.item.voucher_id = voucher.voucher_id;
      this.item.voucher_date = voucher.voucher_date;

      this.voucher_result = [];
      this.itemIndex = 0;
    },

    handleKeysVoucher(event) {
      if (event.key === 'ArrowDown') {
        this.itemIndex = Math.min(this.itemIndex + 1, this.voucher_result.length - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        if (this.voucher_result[this.itemIndex]) {
          this.selectVoucher(this.voucher_result[this.itemIndex]);
        }
        event.preventDefault();
      }
    },

    async searchItems() {
      if (!this.query) {
        this.results = [];
        return;
      }

      if (!this.selectedVoucher) {
        this.toast.warning("Please select Invoice first.");
        return;
      }

      try {
        const response = await this.$axios.get(apiUrls.purchase_voucher_item_search.get, {
          params: {voucher_id: this.item.voucher_id, q: this.query }
        })
        console.log(response.data.results);
        this.results = response.data.results;
        this.itemIndex = 0;

        // render feather icons
        await this.$nextTick(() => {
          if (window.feather) feather.replace()
        })
      } catch (error) {
        this.results = [];
        this.addedItems = [];
      }
    },

    selectItem(item) {
      const totalQty = Number(item.total_qty || 0);
      const formattedUnitPrice = Number(item.unit_price).toFixed(2);
      const formattedTotalPrice = Number(item.total_price).toFixed(2);

      this.selectedItem = item;
      this.query = item.item_name;

      // Check if item already exists in addedItems
      const existingItem = this.addedItems.find(i => i.item_id === item.id);

      if (!totalQty || totalQty <= 0) {
        this.toast.warning("This item has no available quantity!");
        return;
      }

      if (existingItem) {
        this.toast.warning("This item is already added!");
        return;
      }

      // Add selected item to the addedItems list
      this.addedItems.push({
        item_id: item.id,
        reff_id: item.reff_id,
        item_name: item.item_name,
        item_code: item.item_code,
        barcode: item.barcode,
        unit_price: formattedUnitPrice,
        quantity: item.total_qty,
        total_price: formattedTotalPrice,
        images: item.images || [],
        receiving_no: this.item.receiving_no,
        receiving_date: this.item.receiving_date,
        cash_type: this.item.cash_type,
        bank_account: this.item.bank_account,
        return_qty: 1,
        return_quantity: item.return_qty,
        return_total_price: formattedUnitPrice,

      });

      // Clear search results and form input fields
      this.results = [];
      this.itemIndex = 0;
      this.query = "";
    },

    handleKeys(event) {
      if (event.key === 'ArrowDown') {
        this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        const selectedProduct = this.results[this.itemIndex];
        if (selectedProduct) {
          this.selectItem(selectedProduct);
        }
        event.preventDefault();
      }
    },

    async fetchReceivingNo() {
      try {
        let res = await this.$axios.get(apiUrls.purchase_return_receive_number.get, {
          params: { receiving_date: this.item.receiving_date }
        });
        this.item.receiving_no = res.data.receiving_no;
      } catch (err) {
        console.error(err);
      }
    },

    async submitPurchaseReturn() {
      if (!this.addedItems.length) {
        this.toast.warning("Please add at least one item before submitting.");
        return;
      }

      try {
        // Prepare payload with only the required backend fields
        const payload = this.addedItems.map(item => ({
          item_id: item.item_id,
          reff_id: item.reff_id,
          item_name: item.item_name,
          item_code: item.item_code,
          barcode: item.barcode,
          unit_price: Number(item.unit_price).toFixed(2),
          quantity: item.total_qty,
          total_price: Number(item.return_total_price).toFixed(2),
          receiving_no: this.item.receiving_no,
          receiving_date: this.item.receiving_date,
          cash_type: Number(this.item.cash_type),
          bank_account: Number(this.item.bank_account),
          return_qty: Number(item.return_qty),
          voucher_id: this.item.voucher_id
        }));

        // Send clean payload to backend
        const response = await this.$axios.post(apiUrls.purchase_return.post, payload);

        await this.fetchReceivingNo();
        const updatedReceivingNo = this.item.receiving_no;
        this.toast.success("Purchase return successfully submitted!");
        this.$router.push('/purchase/return/list');
        this.$emit("create_success");

        // Reset form after save
        this.item = {
          id: null, voucher_date: '', voucher_id: '', item: null, quantity: 0, total_price: 0, unit_price: 0,
          receiving_no: updatedReceivingNo, receiving_date: this.item.receiving_date,
          cash_type: this.formattedcashtypes.length > 0 ? this.formattedcashtypes[0].id : '', bank_account: '',
          return_total_price: 0
        }

        this.q = '';
        this.query = '';
        this.selectedItem = null;
        this.addedItems = [];

      } catch (error) {
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        }
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
      }
    },

    cancelForm() {
      this.$router.push('/purchase/return/list');
    },

    removeItem(index) {
      this.addedItems.splice(index, 1);
      this.query = '';
    },

    resetForm() {
      // Reset form fields and errors
      this.item = {
        id: null, voucher_date: '', voucher_id: '', item: null, quantity: 0, total_price: 0, unit_price: 0,
        cash_type: this.cash_types.length > 0 ? this.cash_types[0].id : null, receiving_no: this.item.receiving_no,
        receiving_date: this.item.receiving_date, return_total_price: 0
      };
      this.errors = {};
      this.errorMessage = "";
    },

    validateReturnQty(item) {
      if (isNaN(item.return_qty) || item.return_qty < 1) {
        this.toast.info("Purchase quantity must be at least 1.");
        return;
      }

      if (item.return_qty > item.quantity) {
        this.toast.info("Purchase quantity cannot exceed available quantity.");
        return;
      }

      const unit_price = Number(item.unit_price);
      item.return_total_price = (unit_price * item.return_qty).toFixed(2);
    },

    isValidReturn() {
      return this.addedItems.every(item =>
        item.return_qty >= 1 && item.return_qty <= item.quantity
      );
    },
  },
};
</script>


<style scoped>
/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

.readonly-cell {
  background-color: #fffbe6 !important;
  font-weight: bold;
}

.dynamic-cell {
  background-color: #e6fffb !important;
}

</style>

